import { useMutation } from '@apollo/client';
import { translateBackend } from '@assets/i18n/config';
import { datadogLogs } from '@datadog/browser-logs';
import {
  type CreateScheduledDepositTransferInput,
  type CustodianFinancialProduct,
  Frequency,
  type WorkflowCompletion,
} from '@generated/graphql';
import { FeatureFlagTypes } from '@interfaces/organizationFeatureFlag';
import { getSubAccountName } from '@interfaces/subAccount';
import { Loop } from '@mui/icons-material';
import { Box } from '@ovComponents/1-primative/box/box';
import { Stack } from '@ovComponents/1-primative/stack/stack';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Button } from '@ovComponents/2-component/button/button';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import {
  type CustodianDepositTransferContextInput,
  toTransferContextData,
  type WorkflowCustodianDepositTransfer,
} from '@ovComponents/4-module/workflowCompletion/workflowTypes';
import { useGlobalToast } from '@providers/globalToastProvider';
import { useInactivityTimeout } from '@providers/inActivityTimeoutProvider';
import { UserContext, usePermissions } from '@providers/userContextProvider';
import { isFeatureEnabled } from '@utils/common';
import { FlagsmithFlags } from '@utils/flagsmith';
import { getGraphQLErrorMessage } from '@utils/graphQLError';
import { formatMoneyValue } from '@utils/moneyFormatting';
import { updateContextTransfer } from '@utils/updateWorkflowContext';
import { useLocalization } from '@utils/useLocalization';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { GO_TO_STEP } from '../queries';
import { useDepositWorkflowData } from '../utils/useDepositWorkflowData';
import { DisclaimerBox } from './depositCard';
import {
  CREATE_CUSTODIAN_TRANSFER,
  CREATE_DEPOSIT_TRANSFER,
  CREATE_EXTERNAL_TRANSFER,
  CREATE_SCHEDULED_DEPOSIT_TRANSFER,
  TRANSFER_ALL_BETWEEN_SUB_ACCOUNTS,
  TRANSFER_BETWEEN_SUB_ACCOUNTS,
  UPDATE_SCHEDULED_TRANSFER,
} from './depositReview.queries';
import DepositReviewVisual, {
  type DepositReviewSubstepOptions,
} from './depositReview.visual';

type HeaderContextArgs = {
  sessionId?: string;
};

type CustodianDepositTransferResponse = {
  status: string;
  confirmationNumber: string;
  action?: {
    type: string;
    details?: { key: string; value: unknown }[];
  } | null;
};

type WorkflowCompletionStatus =
  | 'success'
  | 'failure'
  | 'error'
  | 'deny'
  | 'silent';

/**
 * Pick the sub-step that "Add another deposit" should navigate back to.
 * Prefers `DEPOSIT_TYPE` (the type-picker screen). Falls back to
 * `DEPOSIT_FORM` for workflows that omit the type picker — e.g. when
 * `defaultTransferType` is configured on `DEPOSIT_FORM` (OVT-6415).
 */
export const findAddAnotherDepositTargetSubStep = (
  workflowCompletion?: Pick<WorkflowCompletion, 'workflow'>,
): { id?: string; type?: string } | undefined => {
  const subSteps = (workflowCompletion?.workflow?.steps ?? []).flatMap(
    (step) => step?.subSteps ?? [],
  );
  return (
    subSteps.find((x) => x?.type === 'DEPOSIT_TYPE') ??
    subSteps.find((x) => x?.type === 'DEPOSIT_FORM')
  );
};

export const DepositReview = ({
  options,
  onNext,
  dummyDeposit,
  setDummyDeposit,
  workflowCompletion,
  setActiveWorkflowCompletion,
}: {
  options: DepositReviewSubstepOptions;
  onNext: any;
  dummyDeposit?: any;
  setDummyDeposit?: any;
  workflowCompletion?: WorkflowCompletion;
  setActiveWorkflowCompletion?: any;
}) => {
  const { t } = useTranslation(['workflowCompletions', 'accountTypes']);
  const { localizedDate } = useLocalization();
  const { activeOrganization, userContext } = useContext(UserContext);
  const { permissions } = usePermissions();
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const { custodianConnection } = useContext(UserContext);
  const {
    applicableData,
    setApplicationContext,
    setWorkflowCompletionContext,
    updateContextWithFields,
    mode,
  } = useDepositWorkflowData(dummyDeposit, setDummyDeposit, workflowCompletion);
  const [loading, setLoading] = useState<boolean>(false);
  const { showToast } = useGlobalToast();
  const usTransferFlowEnabled: boolean =
    activeOrganization.availableFeatureFlags?.includes(
      FeatureFlagTypes.US_TRANSFER_FLOW,
    ) ?? false;
  const enableMultiCurrencyDepositTransfers: boolean =
    activeOrganization.availableFeatureFlags?.includes(
      FeatureFlagTypes.MULTI_CURRENCY_DEPOSIT_TRANSFERS,
    ) ?? false;
  const participantNumberEnabled: boolean =
    usTransferFlowEnabled &&
    (applicableData.subAccount?.account?.type?.includes('USA_') || false);
  const { attemptSilentRefresh } = useInactivityTimeout();

  // Add new states for workflow completion control
  const custodianActionTakenRef = useRef(false);
  // Refs for immediate access to prevent race conditions with React's async state updates
  const workflowCompletedRef = useRef(false);
  const hasErrorRef = useRef(false);

  const recordWorkflowTransfers = useCallback(
    async (transfer: { id: string }) => {
      await setWorkflowData((data) => ({
        ...data,
        currentTransferId: transfer.id,
        createdTransfers: [...(data?.createdTransfers ?? []), transfer],
      }));
    },
    [setWorkflowData],
  );
  const recordWorkflowScheduledTransfers = useCallback(
    async (scheduledTransfer: { id: string }) => {
      await setWorkflowData((data) => ({
        ...data,
        createdScheduledTransfers: [
          ...(data?.createdScheduledTransfers ?? []),
          scheduledTransfer,
        ],
      }));
    },
    [setWorkflowData],
  );

  type ManualProcess = NonNullable<
    (typeof workflowData)['createdExternalTransfers']
  >[number];
  const recordWorkflowExternalTransfer = useCallback(
    async (transfer: ManualProcess) => {
      await setWorkflowData((data) => ({
        ...data,
        createdExternalTransfers: [
          ...(data?.createdExternalTransfers ?? []),
          transfer,
        ],
      }));
    },
    [setWorkflowData],
  );
  const recordWorkflowCustodianDepositTransfer = useCallback(
    async (transfer: WorkflowCustodianDepositTransfer) => {
      await setWorkflowData((data) => ({
        ...data,
        createdCustodianDepositTransfers: [
          ...(data?.createdCustodianDepositTransfers ?? []),
          transfer,
        ],
      }));
    },
    [setWorkflowData],
  );

  const handleWorkflowCompletion = useCallback(
    async (status: WorkflowCompletionStatus = 'success') => {
      if (workflowCompletedRef.current) return; // Prevent double completion

      workflowCompletedRef.current = true; // Immediate access for race condition prevention
      await onNext();
      setLoading(false);

      // Only show toast for non-silent completions
      switch (status) {
        case 'success':
          showToast({
            message: t('depositReview.success'),
            severity: 'success',
          });
          break;
        case 'failure':
          showToast({
            message: t('depositReview.failure'),
            severity: 'error',
          });
          break;
        case 'deny':
          showToast({
            message: t('depositReview.deny'),
            severity: 'error',
          });
          break;
        case 'error':
          showToast({
            message: t('depositReview.error'),
            severity: 'error',
          });
          break;
        case 'silent':
          // Complete workflow without showing any toast
          // Useful when you want to complete the workflow without showing a toast
          // E.g. when redirecting to external apps like a risk challenge app for ATB
          break;
      }
    },
    [onNext, showToast, t],
  );

  const freshTransferContextData = useMemo(
    () => ({
      ...toTransferContextData({}),
      skipDocuments: applicableData.skipDocuments,
      currentDepositTemporaryId: undefined,
    }),
    [applicableData.skipDocuments],
  );

  // TODO (OVT-6217): This helper is unreachable after the Simility removal —
  // `cmid` is always null and the custodian no longer emits `action: CHALLENGE`,
  // so this code is dormant until the replacement fraud engine is wired up. At
  // that point, we can do any needed refactor with applicable context/details.
  const handleCustodianRiskEngineChallenge = async (
    cmid: string | null,
    data: CustodianDepositTransferResponse,
  ) => {
    const { action } = data;
    const challengedTransfersEnabled =
      custodianConnection?.enableChallengedCustodianDepositTransfer === true;
    const challengeURL = custodianConnection?.metadata?.find(
      (m) => m.key === 'challengeUrl',
    )?.value as string;
    const transactionId = action?.details?.find(
      (d) => d.key === 'transactionId',
    )?.value as string;
    if (!custodianConnection?.enableChallengedCustodianDepositTransfer) {
      datadogLogs.logger.warn(
        'Custodian Deposit Transfer - enableChallengedCustodianDepositTransfer is not set. Cannot proceed to challenge',
      );
      await handleWorkflowCompletion('error');
      return;
    }

    if (!challengeURL) {
      datadogLogs.logger.warn(
        'Custodian Deposit Transfer - custodian connection does not have challengeUrl. Cannot proceed to challenge',
      );
      await handleWorkflowCompletion('error');
      return;
    }

    if (!transactionId) {
      datadogLogs.logger.warn(
        'Custodian Deposit Transfer - No transactionId was returned. Cannot proceed to challenge',
      );
      await handleWorkflowCompletion('error');
      return;
    }

    if (action && challengedTransfersEnabled && challengeURL && transactionId) {
      if (!cmid) {
        datadogLogs.logger.warn(
          'Custodian Deposit Transfer - CMID is not set. Cannot proceed to challenge',
        );
        await handleWorkflowCompletion('error');
        return;
      }

      // Refresh access token to reset its validity window
      if (attemptSilentRefresh) {
        attemptSilentRefresh();
      }

      const { protocol, host, pathname } = window.location;
      const inboundRedirectUrl = `${protocol}//${host}${pathname}?atbmeta=transactionId:${transactionId}`;
      const toUrlEncode = `session-id=${cmid}&transaction-id=${transactionId}&redirect=${inboundRedirectUrl}`;
      const outboundRedirectUrl = `${challengeURL}?${encodeURIComponent(toUrlEncode)}`;

      try {
        datadogLogs.logger.info(
          'Custodian Deposit Transfer - will redirect to custodian risk challenge',
          { cmid, transactionId },
        );

        // Complete workflow first (this closes the workflow dialog)
        // then redirect in quick succession
        await handleWorkflowCompletion('silent');
        window.localStorage.setItem(
          'custodianRiskChallengeTransaction',
          transactionId,
        );
        window.location.replace(outboundRedirectUrl);
      } catch (error) {
        datadogLogs.logger.error(
          'Custodian Deposit Transfer - error redirecting to custodian risk challenge',
          { data, error },
        );
        showToast({
          message: t('unableToRedirectForVerification'),
          severity: 'error',
        });
      }
    }
  };

  const handleCustodianRiskEngineActionIfAny = async (
    cmid: string | null,
    data: CustodianDepositTransferResponse,
  ) => {
    const { action } = data || {};
    if (action && action.type) {
      const actionType = action.type.trim().toUpperCase();
      switch (actionType) {
        case 'DENY':
          datadogLogs.logger.info('Custodian Deposit Transfer - DENY', {
            actionType,
            cmid,
          });
          await handleWorkflowCompletion('deny');
          break;
        // TODO (OVT-6217): Unreachable after the Simility removal — the custodian
        // no longer emits `action: CHALLENGE`, so this branch is dormant until the
        // replacement fraud engine is wired up. At that point, we can do any
        // needed refactor with applicable context/details.
        case 'CHALLENGE':
          datadogLogs.logger.info('Custodian Deposit Transfer - CHALLENGE', {
            actionType,
            cmid,
          });
          await handleCustodianRiskEngineChallenge(cmid, data);
          break;
        default:
          datadogLogs.logger.info(
            'Custodian Deposit Transfer - UNKNOWN ACTION',
            { actionType, cmid },
          );
          // For unknown actions, allow the standard workflow
          // completion (i.e success) to take place
          custodianActionTakenRef.current = false;
          break;
      }
    }
  };

  // TODO (OVT-6217): `CMID` is always null after the Simility removal — no
  // code path writes it to sessionStorage, so every downstream branch gated on
  // it in this file is a no-op until the replacement fraud engine is wired up.
  // At that point, we can do any needed refactor with applicable context/details.
  const CMID = window.sessionStorage.getItem('CMID');
  const buildHeaderContext = ({ sessionId }: HeaderContextArgs = {}) =>
    sessionId && typeof sessionId === 'string' && sessionId.trim().length === 32
      ? { headers: { 'session-id': sessionId } }
      : {};

  const [createDepositTransfer] = useMutation<{
    createDepositTransfer: { transfer: { id: string } };
  }>(CREATE_DEPOSIT_TRANSFER);
  const [createScheduledDepositTransferMutation] = useMutation(
    CREATE_SCHEDULED_DEPOSIT_TRANSFER,
  );
  const createScheduledDepositTransfer: typeof createScheduledDepositTransferMutation =
    async (...args) => {
      const result = await createScheduledDepositTransferMutation(...args);
      const skipDocuments = !isFeatureEnabled(FeatureFlagTypes.PAD_AGREEMENTS);
      await updateContextTransfer({
        value: { ...applicableData, skipDocuments },
        existingContext: applicableData,
        setApplicationContext,
        setWorkflowCompletionContext,
      });
      return result;
    };
  const [createTransferBetweenSubAccounts] = useMutation<{
    transferBetweenSubAccounts: { transfer: { id: string } };
  }>(TRANSFER_BETWEEN_SUB_ACCOUNTS);
  const [createTransferAllBetweenSubAccounts] = useMutation<{
    transferAllBetweenSubAccounts: { transfer: { id: string } };
  }>(TRANSFER_ALL_BETWEEN_SUB_ACCOUNTS);
  const [createExternalTransfer] = useMutation<{
    createExternalTransfer: { manualProcess: ManualProcess };
  }>(CREATE_EXTERNAL_TRANSFER);
  const [createCustodianTransfer] = useMutation<{
    createCustodianDepositTransfer: CustodianDepositTransferResponse;
  }>(CREATE_CUSTODIAN_TRANSFER, {
    onError: () => {
      hasErrorRef.current = true;
      handleWorkflowCompletion('failure');
    },
    onCompleted(data) {
      const hasAction =
        data.createCustodianDepositTransfer.action != null &&
        typeof data.createCustodianDepositTransfer.action === 'object';
      if (hasAction) {
        custodianActionTakenRef.current = true;
        handleCustodianRiskEngineActionIfAny(
          CMID,
          data.createCustodianDepositTransfer,
        );
      }
    },
  });

  const [updateScheduledTransferMutation] = useMutation(
    UPDATE_SCHEDULED_TRANSFER,
  );
  const updateScheduledTransfer: typeof updateScheduledTransferMutation =
    async (...args) => {
      const result = await updateScheduledTransferMutation(...args);
      const skipDocuments = !isFeatureEnabled(FeatureFlagTypes.PAD_AGREEMENTS);
      await updateContextTransfer({
        value: { ...applicableData, skipDocuments },
        existingContext: applicableData,
        setApplicationContext,
        setWorkflowCompletionContext,
      });
      return result;
    };

  const [goToStep] = useMutation(GO_TO_STEP(permissions));
  const toast = useGlobalToast();

  const fullTransfer = () =>
    applicableData?.fromSubAccount?.statistics?.marketValueCents &&
    (applicableData?.amountCents || 0) >
      (applicableData?.fromSubAccount?.statistics?.marketValueCents || 0) *
        0.97;

  const isMultipleDepositsEnabled =
    activeOrganization.availableFeatureFlags?.includes(
      FlagsmithFlags.multi_deposit_review,
    );

  const submit = async () => {
    setLoading(true);
    custodianActionTakenRef.current = false;
    hasErrorRef.current = false;

    // Deferred external deposit transfer: save input to context and advance
    // to the SynchronousIntegration step. The Windmill script will handle
    // the actual custodian deposit transfer API call.
    //
    // Multiple deposits are not supported because each custodian challenge
    // redirects the user externally for MFA, which cannot be serialized
    // across multiple deposits in a single workflow session.
    //
    // See: https://onevest.getoutline.com/doc/business-case-workflow-engine-interactivity-dynamic-response-handling-JeG4OzzXDZ
    if (
      options.deferredExternalDepositTransfer &&
      options.createExternalDepositTransfer
    ) {
      if (!CMID) {
        datadogLogs.logger.warn('Custodian deposit transfer: CMID is missing');
      }

      try {
        const deposit = applicableData;
        const scheduledDate = deposit?.scheduledDate;

        // Create the internal OV deposit record if the org has opted in.
        if (
          custodianConnection?.enableSaveInternalRecordsOfCustodianDepositTransfers ===
          true
        ) {
          const { data } = await createDepositTransfer({
            variables: {
              input: {
                amountCents: deposit?.amountCents,
                externalBankAccountId: deposit?.bankAccount?.id,
                subAccountId: deposit?.subAccount?.id,
                fiscalYear: deposit?.fiscalYear,
                ...(enableMultiCurrencyDepositTransfers
                  ? { currency: deposit?.bankAccount?.currency }
                  : {}),
              },
            },
          });
          if (data?.createDepositTransfer?.transfer) {
            await recordWorkflowTransfers(data.createDepositTransfer.transfer);
          }
        }

        const custodianDepositTransferInput: CustodianDepositTransferContextInput =
          {
            cmid: CMID || undefined,
            accountId: deposit?.subAccount?.account?.id,
            organizationUserId: userContext?.id,
            amount: deposit?.amountCents,
            fund: deposit?.holdings?.financialProduct?.cusip ?? undefined,
            frequency: deposit?.schedule,
            startDate:
              scheduledDate instanceof Date
                ? scheduledDate.toISOString()
                : scheduledDate,
            bankAccount: {
              accountNumber: deposit?.bankAccount?.bankAccountNumberDisclosed,
              institutionNumber: deposit?.bankAccount?.institutionNumber,
              transitNumber: deposit?.bankAccount?.transitNumberDisclosed,
            },
          };

        await setWorkflowData((prev) => ({
          ...prev,
          custodianDepositTransfer: { input: custodianDepositTransferInput },
        }));

        await onNext();
        // Intentionally do NOT reset loading. The component unmounts
        // when the workflow advances, and keeping the button disabled
        // prevents double-click race conditions.
      } catch (e: unknown) {
        const errorMessage = e instanceof Error ? e.message : undefined;
        datadogLogs.logger.error(
          'Deferred custodian deposit transfer: failed to save transfer input to context',
          { error: e },
        );
        showToast({
          message: errorMessage || t('depositReview.error'),
          severity: 'error',
        });
        setLoading(false);
      }
      return;
    }

    const temporaryDeposits = isMultipleDepositsEnabled
      ? applicableData.temporaryDeposits || [applicableData]
      : [applicableData];

    const failedTemporaryDeposits = [] as Array<{
      deposit: typeof applicableData;
      err: Error;
    }>;
    const referenceIds: Record<string, string | undefined> = {};
    const depositsToSubmit = temporaryDeposits?.filter(
      (deposit) => deposit.temporaryTransferState !== 'completed',
    );

    if (depositsToSubmit.length === 0) {
      // Early return if no deposits to submit - complete workflow silently without showing success toast
      await updateContextWithFields(freshTransferContextData);
      await handleWorkflowCompletion('silent');
      setLoading(false);
      return;
    }

    for (const deposit of depositsToSubmit) {
      const { temporaryId } = deposit;
      try {
        if (mode === 'edit') {
          await updateScheduledTransfer({
            variables: {
              input: {
                scheduledTransferId: deposit?.scheduledTransferId ?? '',
                scheduledDate: deposit?.scheduledDate as string,
                frequency: deposit?.schedule as Frequency,
                amountCents: deposit?.amountCents,
                bankAccountId: deposit?.bankAccount?.id,
              },
            },
          });
        } else {
          if (deposit?.type === 'EFT') {
            if (deposit?.schedule === 'ONE_TIME') {
              // non scheduled
              if (deposit?.scheduledDate) {
                const input = {
                  frequency: Frequency.OneTime,
                  amountCents: deposit?.amountCents || 0,
                  bankAccountId: !options.createExternalDepositTransfer
                    ? deposit?.bankAccount?.id
                    : undefined,
                  externalBankAccountId: options.createExternalDepositTransfer
                    ? deposit?.bankAccount?.id
                    : undefined,
                  subAccountId: deposit?.subAccount?.id as string,
                  scheduledDate: deposit?.scheduledDate as string,
                  fiscalYear: deposit?.fiscalYear ?? undefined,
                  ...(enableMultiCurrencyDepositTransfers &&
                  deposit?.bankAccount?.currency
                    ? { currency: deposit?.bankAccount?.currency }
                    : {}),
                } as CreateScheduledDepositTransferInput;

                const { data } = await createScheduledDepositTransfer({
                  variables: { input },
                });

                if (data?.createScheduledDepositTransfer?.scheduledTransfer) {
                  const {
                    createScheduledDepositTransfer: { scheduledTransfer },
                  } = data;
                  if (temporaryId)
                    referenceIds[temporaryId] = scheduledTransfer.id;
                  await recordWorkflowScheduledTransfers(scheduledTransfer);
                }
              } else {
                if (
                  !options.createExternalDepositTransfer ||
                  (options.createExternalDepositTransfer &&
                    custodianConnection?.enableSaveInternalRecordsOfCustodianDepositTransfers ===
                      true)
                ) {
                  const input = {
                    amountCents: deposit?.amountCents,
                    bankAccountId: !options.createExternalDepositTransfer
                      ? deposit?.bankAccount?.id
                      : undefined,
                    externalBankAccountId: options.createExternalDepositTransfer
                      ? deposit?.bankAccount?.id
                      : undefined,
                    subAccountId: deposit?.subAccount?.id,
                    fiscalYear: deposit?.fiscalYear,
                    ...(enableMultiCurrencyDepositTransfers
                      ? { currency: deposit?.bankAccount?.currency }
                      : {}),
                  };

                  const { data } = await createDepositTransfer({
                    variables: { input },
                  });
                  if (data?.createDepositTransfer?.transfer) {
                    const {
                      createDepositTransfer: { transfer },
                    } = data;
                    if (temporaryId) referenceIds[temporaryId] = transfer.id;
                    await recordWorkflowTransfers(transfer);
                  }
                }

                if (options?.createExternalDepositTransfer) {
                  const input = {
                    accountId: deposit?.subAccount?.account?.id,
                    amount: deposit?.amountCents,
                    fund: deposit?.holdings?.financialProduct?.cusip,
                    frequency: deposit?.schedule,
                    startDate: deposit?.scheduledDate,
                    bankAccount: {
                      accountNumber:
                        deposit?.bankAccount?.bankAccountNumberDisclosed,
                      institutionNumber:
                        deposit?.bankAccount?.institutionNumber,
                      transitNumber:
                        deposit?.bankAccount?.transitNumberDisclosed,
                    },
                  };
                  const { data } = await createCustodianTransfer({
                    variables: { input },
                    ...(CMID && {
                      context: {
                        ...buildHeaderContext({ sessionId: CMID }),
                      },
                    }),
                  });
                  if (data?.createCustodianDepositTransfer) {
                    const {
                      createCustodianDepositTransfer: custodianDepositTransfer,
                    } = data;
                    if (temporaryId)
                      referenceIds[temporaryId] =
                        custodianDepositTransfer.confirmationNumber;

                    await recordWorkflowCustodianDepositTransfer({
                      ...input,
                      confirmationNumber:
                        custodianDepositTransfer.confirmationNumber,
                    });
                  } else {
                    datadogLogs.logger.error(
                      'Custodian deposit transfer returned no data',
                      { input },
                    );
                  }
                }
              }
            } else {
              // scheduled
              if (
                !options.createExternalDepositTransfer ||
                (options.createExternalDepositTransfer &&
                  custodianConnection?.enableSaveInternalRecordsOfCustodianDepositTransfers ===
                    true)
              ) {
                const input = {
                  amountCents: deposit?.amountCents,
                  bankAccountId: !options.createExternalDepositTransfer
                    ? deposit?.bankAccount?.id
                    : undefined,
                  externalBankAccountId: options.createExternalDepositTransfer
                    ? deposit?.bankAccount?.id
                    : undefined,
                  subAccountId: deposit?.subAccount?.id,
                  scheduledDate: deposit?.scheduledDate,
                  frequency: deposit?.schedule,
                  fiscalYear: deposit?.fiscalYear ?? undefined,
                  ...(enableMultiCurrencyDepositTransfers
                    ? { currency: deposit?.bankAccount?.currency }
                    : {}),
                } as CreateScheduledDepositTransferInput;

                const { data } = await createScheduledDepositTransfer({
                  variables: { input },
                });
                if (data?.createScheduledDepositTransfer?.scheduledTransfer) {
                  const {
                    createScheduledDepositTransfer: { scheduledTransfer },
                  } = data;
                  if (temporaryId)
                    referenceIds[temporaryId] = scheduledTransfer.id;
                  await recordWorkflowScheduledTransfers(scheduledTransfer);
                }
              }

              if (options?.createExternalDepositTransfer) {
                const input = {
                  accountId: deposit?.subAccount?.account?.id,
                  amount: deposit?.amountCents,
                  fund: deposit?.holdings?.financialProduct?.cusip,
                  frequency: deposit?.schedule,
                  startDate: deposit?.scheduledDate,
                  bankAccount: {
                    accountNumber:
                      deposit?.bankAccount?.bankAccountNumberDisclosed,
                    institutionNumber: deposit?.bankAccount?.institutionNumber,
                    transitNumber: deposit?.bankAccount?.transitNumberDisclosed,
                  },
                };
                const { data } = await createCustodianTransfer({
                  variables: { input },
                  ...(CMID && {
                    context: {
                      ...buildHeaderContext({ sessionId: CMID }),
                    },
                  }),
                });
                if (data?.createCustodianDepositTransfer) {
                  const { createCustodianDepositTransfer } = data;
                  await recordWorkflowCustodianDepositTransfer({
                    ...input,
                    confirmationNumber:
                      createCustodianDepositTransfer.confirmationNumber,
                  });
                } else {
                  datadogLogs.logger.error(
                    'Custodian Scheduled deposit transfer returned no data',
                    { input },
                  );
                }
              }
            }
          } else if (deposit?.type === 'INTERNAL_TRANSFER') {
            if (!fullTransfer()) {
              const input = {
                amountCents: deposit?.amountCents,
                fromSubAccountId: deposit?.fromSubAccount?.id,
                toSubAccountId: deposit?.subAccount?.id,
                fiscalYear: deposit?.fiscalYear ?? undefined,
                ...(enableMultiCurrencyDepositTransfers && deposit?.currency
                  ? {
                      currency: deposit?.currency,
                    }
                  : {}),
              };

              const { data } = await createTransferBetweenSubAccounts({
                variables: { input },
              });
              if (data?.transferBetweenSubAccounts?.transfer) {
                const {
                  transferBetweenSubAccounts: { transfer },
                } = data;
                await recordWorkflowTransfers(transfer);
              }
            } else {
              const input = {
                fromSubAccountId: deposit?.fromSubAccount?.id,
                toSubAccountId: deposit?.subAccount?.id,
                fiscalYear: deposit?.fiscalYear ?? undefined,
                ...(enableMultiCurrencyDepositTransfers && deposit?.currency
                  ? {
                      currency: deposit?.currency,
                    }
                  : {}),
              };

              const { data } = await createTransferAllBetweenSubAccounts({
                variables: { input },
              });
              if (data?.transferAllBetweenSubAccounts?.transfer) {
                const {
                  transferAllBetweenSubAccounts: { transfer },
                } = data;
                if (temporaryId) referenceIds[temporaryId] = transfer.id;
                await recordWorkflowTransfers(transfer);
              }
            }
          } else {
            const input = {
              amountCents: deposit?.amountCents,
              subAccountId: deposit?.subAccount?.id,
              institution: { id: deposit?.institution?.id },
              transferAccountNumber: deposit?.accountNumber,
              transferMethod: deposit?.transferType,
              transferAccount: deposit?.accountType,
              participantNumber:
                participantNumberEnabled && deposit?.participantNumber
                  ? deposit?.participantNumber
                  : undefined,
            };

            const { data } = await createExternalTransfer({
              variables: { input },
            });
            if (data?.createExternalTransfer?.manualProcess?.id) {
              const {
                createExternalTransfer: { manualProcess },
              } = data;
              if (temporaryId) referenceIds[temporaryId] = manualProcess.id;
              await recordWorkflowExternalTransfer(manualProcess);
            }
          }
        }
      } catch (err) {
        failedTemporaryDeposits.push({ deposit, err: err as Error });
      }
    }

    const currentDepositSucceeded = !failedTemporaryDeposits.some(
      (_d) =>
        _d.deposit.temporaryId === applicableData.currentDepositTemporaryId,
    );

    await updateContextWithFields({
      ...(currentDepositSucceeded ? freshTransferContextData : {}),
      temporaryDeposits: temporaryDeposits.map((d) =>
        failedTemporaryDeposits.some(
          (_d) => d.temporaryId === _d.deposit.temporaryId,
        )
          ? ({
              ...d,
              temporaryTransferState: 'failed',
              referenceId: d.temporaryId
                ? referenceIds[d.temporaryId]
                : undefined,
            } as typeof d)
          : ({
              ...d,
              temporaryTransferState: 'completed',
              referenceId: d.temporaryId
                ? referenceIds[d.temporaryId]
                : undefined,
            } as typeof d),
      ),
    });
    if (failedTemporaryDeposits.length) {
      setLoading(false);
      datadogLogs.logger.warn(
        'multi deposit review could not complete all deposits',
        {
          workflowCompletion,
          applicableData,
        },
      );
      const validationMessage = failedTemporaryDeposits
        .map(({ err }) => getGraphQLErrorMessage(err))
        .find((m): m is string => Boolean(m));
      toast.showToast({
        message:
          validationMessage ?? t('depositReview.couldNotCompleteAllDeposits'),
        severity: 'error',
      });
      return;
    }
    if (
      !custodianActionTakenRef.current &&
      !workflowCompletedRef.current &&
      !hasErrorRef.current
    ) {
      // Only complete workflow if no custodian action was taken and no error occurred.
      // Custodian actions handle completion in their onCompleted callbacks.
      // Using refs to prevent race conditions with React's async state updates
      await handleWorkflowCompletion('success');
    } else {
      await handleWorkflowCompletion('silent');
    }
  };

  const addAnotherDeposit = async () => {
    setLoading(true);
    const targetSubStep =
      findAddAnotherDepositTargetSubStep(workflowCompletion);

    await updateContextWithFields({
      ...toTransferContextData({}),
      currentDepositTemporaryId: undefined,
    });

    const { data: goToStepData } = await goToStep({
      variables: {
        input: {
          workflowCompletionId: workflowCompletion?.id,
          subStepId: targetSubStep?.id,
        },
      },
    });

    setActiveWorkflowCompletion(goToStepData.goToSubStep.workflowCompletion);
    setLoading(false);
  };

  if (isMultipleDepositsEnabled) {
    return (
      <WorkflowCompletionForm
        onSubmit={submit}
        buttonFooter={
          options.enableAddAnotherDeposit && (
            <Box display='flex' justifyContent='flex-start' flex={1}>
              <Button
                dataTestId='deposit-review-add-another-button'
                label={t('depositReview.addAnotherDeposit')}
                variant='filled'
                sx={{ mt: 3, mr: 1 }}
                disabled={loading}
                onClick={addAnotherDeposit}
                leadingIcon={Loop}
              />
            </Box>
          )
        }
        submitButtonProps={{
          label: applicableData.temporaryDeposits?.every(
            (d) => d.temporaryTransferState === 'completed',
          )
            ? t('depositReview.continue')
            : t('depositReview.createDeposit'),
          disabled: loading,
          dataTestId: 'deposit-review-continue-button',
        }}
      >
        <Typography variant='displayLarge' sx={{ mt: 1 }}>
          {translateBackend(options?.title)}
        </Typography>
        {options?.subtitle && (
          <Typography variant='bodyLarge' sx={{ mb: 3 }}>
            <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
              {translateBackend(options?.subtitle)}
            </ReactMarkdown>
          </Typography>
        )}
        <DepositReviewVisual
          options={options}
          dummyDeposit={dummyDeposit}
          setDummyDeposit={setDummyDeposit}
          workflowCompletion={workflowCompletion}
          disableInteractions={loading}
        />
      </WorkflowCompletionForm>
    );
  }

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      submitButtonProps={{
        label:
          mode === 'edit'
            ? t('depositReview.updateDeposit')
            : t('depositReview.createDeposit'),
        disabled: loading,
        dataTestId: 'deposit-review-continue-button',
      }}
    >
      <Typography variant='displayLarge' sx={{ mt: 1 }}>
        {translateBackend(options?.title)}
      </Typography>
      {options?.subtitle && (
        <Typography variant='bodyLarge' sx={{ mb: 3 }}>
          <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
            {translateBackend(options?.subtitle)}
          </ReactMarkdown>
        </Typography>
      )}
      {applicableData?.type === 'EFT' && (
        <Stack spacing={3}>
          <Box>
            <Typography variant='labelLarge' colorVariant='variant'>
              {t('depositReview.fromBankAccount')}
            </Typography>
            <Typography
              variant='bodyLarge'
              weight='bold'
            >{`${applicableData?.bankAccount?.name} ${applicableData?.bankAccount?.bankAccountNumber}`}</Typography>
          </Box>
          <Box>
            <Typography variant='labelLarge' colorVariant='variant'>
              {t('depositReview.toSubAccount')}
            </Typography>
            <Typography variant='bodyLarge' weight='bold'>
              {getSubAccountName(applicableData?.subAccount)}
            </Typography>
          </Box>
          {options?.showHoldings && (
            <Box>
              <Typography variant='labelLarge' colorVariant='variant'>
                {t('depositReview.toHolding')}
              </Typography>
              <Typography variant='bodyLarge' weight='bold'>
                {translateBackend(
                  (
                    applicableData?.holdings
                      ?.financialProduct as CustodianFinancialProduct
                  )?.translatedNickName ??
                    applicableData?.holdings?.financialProduct?.translatedName,
                )}
              </Typography>
            </Box>
          )}
          <Box>
            <Typography variant='labelLarge' colorVariant='variant'>
              {t('depositReview.amount')}
            </Typography>
            <Typography variant='bodyLarge' weight='bold'>
              {formatMoneyValue(applicableData?.amountCents, '$', 2, false)}
            </Typography>
          </Box>
          {applicableData?.schedule !== 'ONE_TIME' && (
            <>
              <Box>
                <Typography variant='labelLarge' colorVariant='variant'>
                  {t('depositReview.schedule')}
                </Typography>
                <Typography variant='bodyLarge' weight='bold'>
                  {t(`schedule.${applicableData?.schedule}`)}
                </Typography>
              </Box>
              <Box>
                <Typography variant='labelLarge' colorVariant='variant'>
                  {t('depositReview.startsOn')}
                </Typography>
                <Typography variant='bodyLarge' weight='bold'>
                  {localizedDate(applicableData?.scheduledDate)}
                </Typography>
              </Box>
            </>
          )}
          <DisclaimerBox text={translateBackend(options?.eftDisclaimer)} />
        </Stack>
      )}
      {applicableData?.type === 'INTERNAL_TRANSFER' && (
        <Stack spacing={3}>
          <Box>
            <Typography variant='labelLarge' colorVariant='variant'>
              {t('depositReview.fromSubAccount')}
            </Typography>
            <Typography variant='bodyLarge' weight='bold'>
              {getSubAccountName(applicableData?.fromSubAccount)}
            </Typography>
          </Box>
          <Box>
            <Typography variant='labelLarge' colorVariant='variant'>
              {t('depositReview.toSubAccount')}
            </Typography>
            <Typography variant='bodyLarge' weight='bold'>
              {getSubAccountName(applicableData?.subAccount)}
            </Typography>
          </Box>
          <Box>
            <Typography variant='labelLarge' colorVariant='variant'>
              {t('depositReview.amount')}
            </Typography>
            <Typography variant='bodyLarge' weight='bold'>
              {fullTransfer()
                ? t('depositReview.fullTransfer')
                : formatMoneyValue(applicableData?.amountCents, '$', 2, false)}
            </Typography>
          </Box>
          <DisclaimerBox
            text={translateBackend(options?.internalTransferDisclaimer)}
          />
        </Stack>
      )}
      {applicableData?.type === 'EXTERNAL_TRANSFER' && (
        <Stack spacing={3}>
          <Box>
            <Typography variant='labelLarge' colorVariant='variant'>
              {t('depositReview.toSubAccount')}
            </Typography>
            <Typography variant='bodyLarge' weight='bold'>
              {getSubAccountName(applicableData?.subAccount)}
            </Typography>
          </Box>
          <Box>
            <Typography variant='labelLarge' colorVariant='variant'>
              {t('depositReview.financialInstitution')}
            </Typography>
            <Typography variant='bodyLarge' weight='bold'>
              {applicableData?.institution?.name}
            </Typography>
          </Box>
          <Box>
            <Typography variant='labelLarge' colorVariant='variant'>
              {t('depositReview.accountNumber')}
            </Typography>
            <Typography variant='bodyLarge' weight='bold'>
              {applicableData?.accountNumber}
            </Typography>
          </Box>
          <Box>
            <Typography variant='labelLarge' colorVariant='variant'>
              {t('depositReview.accountType')}
            </Typography>
            <Typography variant='bodyLarge' weight='bold'>
              {applicableData?.accountType
                ? t(`accountTypes:${applicableData.accountType}`)
                : null}
            </Typography>
          </Box>
          <Box>
            <Typography variant='labelLarge' colorVariant='variant'>
              {t('depositReview.transferType')}
            </Typography>
            <Typography variant='bodyLarge' weight='bold'>
              {t(`depositReview.${applicableData?.transferType}`)}
            </Typography>
          </Box>
          <Box>
            <Typography variant='labelLarge' colorVariant='variant'>
              {t('depositReview.amount')}
            </Typography>
            <Typography variant='bodyLarge' weight='bold'>
              {fullTransfer()
                ? t('depositReview.fullTransfer')
                : formatMoneyValue(applicableData?.amountCents, '$', 2, false)}
            </Typography>
          </Box>
          <DisclaimerBox
            text={translateBackend(options?.externalTransferDisclaimer)}
          />
        </Stack>
      )}
    </WorkflowCompletionForm>
  );
};

export default DepositReview;
